import type { WaitForOptions } from '@trigger.dev/sdk';
import stableStringify from 'json-stable-stringify';
import { system } from '@/code/lib/system-logger';
import invariant from '@/code/lib/utils/invariant';

// This module is transitively pulled into client bundles, so nothing
// server-only may load at module scope: the Trigger SDK, node `crypto`, and
// `async_hooks` are all resolved lazily and only on the server.
let triggerSdk: typeof import('@trigger.dev/sdk') | null | undefined;
function resolveTriggerSdk(): typeof import('@trigger.dev/sdk') | null {
  if (triggerSdk === undefined) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      triggerSdk =
        require('@trigger.dev/sdk') as typeof import('@trigger.dev/sdk');
    } catch {
      triggerSdk = null; // browser / edge — no SDK, never in a Trigger worker
    }
  }
  return triggerSdk;
}

export function isInTrigger() {
  try {
    return !!resolveTriggerSdk()?.metadata.current();
  } catch (_err) {
    return false; // if it fails, we are not in a trigger
  }
}

// When a parent task fans out work via Promise.all/allSettled (e.g. the
// shadow-variant runner), Trigger.dev's `wait.for()` rejects with
// TASK_DID_CONCURRENT_WAIT — but only after a ~2-min internal timeout. The
// runner wraps each branch in `runWithForcedJsTimeout` so any nested
// `waitFor` (AIService rate-limit, Unipile retry, optimizedNetworkCall, …)
// skips the durable wait and uses setTimeout instead.
type ForceJsTimeoutALS = {
  run: <T>(value: true, fn: () => Promise<T>) => Promise<T>;
  getStore: () => true | undefined;
};

const forceJsTimeoutALS: ForceJsTimeoutALS =
  typeof window === 'undefined'
    ? // eslint-disable-next-line @typescript-eslint/no-require-imports
      new (
        require('async_hooks') as typeof import('async_hooks')
      ).AsyncLocalStorage<true>()
    : { run: (_value, fn) => fn(), getStore: () => undefined };

export function runWithForcedJsTimeout<T>(fn: () => Promise<T>): Promise<T> {
  return forceJsTimeoutALS.run(true, fn);
}

export function waitOptionsToMs(options: WaitForOptions): number {
  if ('seconds' in options) {
    return options.seconds * 1000;
  } else if ('minutes' in options) {
    return options.minutes * 60 * 1000;
  } else if ('hours' in options) {
    return options.hours * 60 * 60 * 1000;
  } else if ('days' in options) {
    return options.days * 24 * 60 * 60 * 1000;
  } else if ('weeks' in options) {
    return options.weeks * 7 * 24 * 60 * 60 * 1000;
  } else if ('months' in options) {
    // Assuming an average month length of 30.44 days
    return options.months * 30.44 * 24 * 60 * 60 * 1000;
  } else if ('years' in options) {
    // Assuming 365.25 days per year (accounting for leap years)
    return options.years * 365.25 * 24 * 60 * 60 * 1000;
  } else {
    // This case should never be reached if the WaitOptions type is used correctly
    throw new Error('Invalid WaitForOptions');
  }
}

export async function waitFor(
  params: WaitForOptions,
  forceJsTimeout = false,
): Promise<void> {
  if (process.env.NODE_ENV === 'test') {
    system.log(
      `In test environment. Skipping wait for ${JSON.stringify(params)}`,
    );
    return;
  }
  const useTrigger =
    isInTrigger() && !forceJsTimeout && !forceJsTimeoutALS.getStore();
  if (useTrigger) {
    try {
      system.info('Trigger Waiting for', params);
      const sdk = resolveTriggerSdk();
      invariant(sdk, 'isInTrigger() implies the Trigger SDK is loadable');
      await sdk.wait.for(params);
    } catch (err) {
      system.error(
        'Somehow trigger wait failed, falling back to jswait',
        params,
        err,
      );
      await waitFor(params, true);
    }
  } else {
    system.log(`Using js wait for ${JSON.stringify(params)}`);
    await new Promise((resolve) =>
      setTimeout(resolve, waitOptionsToMs(params)),
    );
  }
}

type FailResponse = 'throw' | 'log' | 'silent';

function hashInputData(data: string | object): string {
  // Server-only helper — load node crypto at call time so this module still
  // evaluates in browser bundles.
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const { createHash } = require('crypto') as typeof import('crypto');
  // check if data is of type object or type string
  if (data === null || typeof data !== 'object') {
    // If data is not an object, we can directly return its stringified hash
    return createHash('sha256').update(JSON.stringify(data)).digest('hex');
  }

  const stableJsonString = stableStringify(data);
  invariant(stableJsonString, 'Failed to stringify input object');

  return createHash('sha256').update(stableJsonString).digest('hex');
}

/**
 * Waits for a specified duration and validates that the application state remains unchanged
 * during the wait period by comparing SHA-256 hashes of the state before and after waiting.
 *
 * This function is useful for ensuring system stability during delays, verifying that
 * background processes don't modify critical state, or validating that external systems
 * remain consistent during waiting periods.
 *
 * @param options - Configuration object for the wait and validation behavior
 * @param options.time - The duration to wait. Can be a number (milliseconds) or an object
 *                       with time units like `{ seconds: 30, minutes: 1 }`
 * @param options.inputHashFunction - Function that returns the state to be validated.
 *                                    Can return a string, object, or Promise resolving to either.
 *                                    This function is called twice: once before waiting and once after.
 * @param options.failResponse - How to handle validation failures:
 *                               - 'throw' (default): Throws an Error if state changes are detected
 *                               - 'log': Logs a warning message but continues execution
 *                               - 'silent': Fails silently with no output
 * @param options.forceJsTimeout - Whether to force JavaScript setTimeout instead of system timing
 *
 * @returns Promise that resolves when the wait is complete and validation passes (if applicable)
 *
 * @throws {Error} When failResponse is 'throw' and:
 *                 - The initial state cannot be retrieved
 *                 - State validation fails (hashes don't match)
 *                 - An error occurs during final state validation
 *
 * @example
 * // Database state validation
 * await waitForValidate({
 *   time: { seconds: 30 },
 *   inputHashFunction: async () => {
 *     const result = await db.query('SELECT COUNT(*) FROM users');
 *     return { userCount: result[0].count };
 *   },
 *   failResponse: 'throw'
 * });
 *
 * @example
 * // API endpoint validation with logging
 * await waitForValidate({
 *   time: 5000, // 5 seconds
 *   inputHashFunction: async () => {
 *     const response = await fetch('/api/status');
 *     return await response.json();
 *   },
 *   failResponse: 'log'
 * });
 *
 * @example
 * // Local state validation (silent failure)
 * const globalState = { counter: 0, status: 'ready' };
 * await waitForValidate({
 *   time: { minutes: 1 },
 *   inputHashFunction: () => globalState,
 *   failResponse: 'silent'
 * });
 *
 * @example
 * // Simple string state validation
 * await waitForValidate({
 *   time: 2000,
 *   inputHashFunction: () => process.env.NODE_ENV,
 *   failResponse: 'throw'
 * });
 */
export async function waitForValidate({
  time,
  inputHashFunction,
  failResponse = 'throw',
  forceJsTimeout = false,
}: {
  time: WaitForOptions;
  inputHashFunction: () => Promise<string | object> | string | object;
  failResponse?: FailResponse;
  forceJsTimeout?: boolean;
}): Promise<void> {
  let beforeHash: string | null = null;

  // Get initial state hash if validation function is provided
  try {
    const beforeData = await inputHashFunction();
    beforeHash = hashInputData(beforeData);
    system.log(`Initial state hash: ${beforeHash}`);
  } catch (error) {
    system.log(`Error getting initial state: ${error}`);
    if (failResponse === 'throw') {
      throw new Error(`Failed to get initial state for validation: ${error}`);
    }
  }

  system.log(`Waiting for ${JSON.stringify(time)}`);
  await waitFor(time, forceJsTimeout);
  system.log(`Waited for ${JSON.stringify(time)}`);

  // Validate state hasn't changed if validation function was provided
  try {
    const afterData = await inputHashFunction();
    const afterHash = hashInputData(afterData);
    system.log(`Final state hash: ${afterHash}`);

    if (beforeHash !== afterHash) {
      const errorMessage = `State validation failed: Expected hash ${beforeHash}, got ${afterHash}`;

      switch (failResponse) {
        case 'throw':
          throw new Error(errorMessage);
        case 'log':
          system.log(`WARNING: ${errorMessage}`);
          break;
        case 'silent':
          // Do nothing
          break;
        case undefined:
          break;
      }
    } else {
      system.log('State validation passed: No changes detected');
    }
  } catch (error) {
    if (
      error instanceof Error &&
      error.message.includes('State validation failed')
    ) {
      throw error;
    }

    const errorMessage = `Error during state validation: ${error}`;
    switch (failResponse) {
      case 'throw':
        throw new Error(errorMessage);
      case 'log':
        system.log(`WARNING: ${errorMessage}`);
        break;
      case 'silent':
        break;
      case undefined:
        break;
    }
  }
}
